import { useEffect, useRef, useState } from 'react'

const PROGRESS_CAP = 99
const RAMP_DURATION_MS = 4000
const COMPLETE_DURATION_MS = 650

function easeOutCubic(t: number) {
  return 1 - (1 - t) ** 3
}

/** Time-based upload progress: 0→99 over ~4s, then smoothly to 100 when complete. */
export function useDisplayUploadProgress(isComplete: boolean) {
  const [display, setDisplay] = useState(0)
  const displayRef = useRef(0)
  const rafRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    const setDisplayValue = (value: number) => {
      displayRef.current = value
      setDisplay(value)
    }

    const cancel = () => {
      if (rafRef.current !== undefined) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = undefined
      }
    }

    cancel()

    if (isComplete) {
      const from = displayRef.current
      const start = performance.now()

      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / COMPLETE_DURATION_MS)
        setDisplayValue(from + (100 - from) * easeOutCubic(t))
        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick)
        }
      }

      rafRef.current = requestAnimationFrame(tick)
      return cancel
    }

    setDisplayValue(0)
    const start = performance.now()

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / RAMP_DURATION_MS)
      setDisplayValue(t * PROGRESS_CAP)
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return cancel
  }, [isComplete])

  return display
}
